import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Save,
  X,
  DollarSign,
  Package,
  Filter,
  Power,
  LocateFixed,
  Link2,
} from 'lucide-react';
import { showToast } from '../utils/toast';

const ExtrasManagement = () => {
  const [extras, setExtras] = useState([]);
  const [filteredExtras, setFilteredExtras] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [associationExtra, setAssociationExtra] = useState(null);
  const [associationProducts, setAssociationProducts] = useState([]);
  const [associationDraft, setAssociationDraft] = useState({});
  const [associationInitial, setAssociationInitial] = useState({});
  const [associationSearch, setAssociationSearch] = useState('');
  const [associationLoading, setAssociationLoading] = useState(false);
  const [associationSaving, setAssociationSaving] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () =>
      setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // État du formulaire
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Général',
    base_price: 0,
    is_active: true,
    track_stock: false,
    inventory_kind: '',
  });

  const [trackingKindPrompt, setTrackingKindPrompt] = useState(null); // { extra }
  const [trackingKindChoice, setTrackingKindChoice] = useState('composed');

  // Charger les catégories d'extras
  const loadCategories = async () => {
    try {
      const response = await fetch('/api/extra-categories');
      if (response.ok) {
        const data = await response.json();
        // Transformer les données pour correspondre au format attendu
        const formattedCategories = data.map(cat => ({
          value: cat.name,
          label: cat.name,
          color: 'bg-gray-100 text-gray-800'
        }));
        setCategories(formattedCategories);
      }
    } catch (error) {
      console.error('❌ Erreur chargement catégories extras:', error);
      // Fallback vers les catégories par défaut en cas d'erreur
      setCategories([
        { value: 'Général', label: 'Général', color: 'bg-gray-100 text-gray-800' }
      ]);
    }
  };

  // Charger les extras
  const loadExtras = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/extras');
      if (response.ok) {
        const data = await response.json();
        const normalized = data.map((extra) => ({
          ...extra,
          track_stock: Boolean(extra.track_stock),
        }));
        setExtras(normalized);
        setFilteredExtras(normalized);
      }
    } catch (error) {
      console.error('❌ Erreur chargement extras:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convertir une couleur hex en classe Tailwind
  const getCategoryColorClass = (hexColor) => {
    // Retourner une couleur par défaut si hexColor n'existe pas
    if (!hexColor) {
      return 'bg-gray-100 text-gray-800';
    }

    const colorMap = {
      '#F59E0B': 'bg-amber-100 text-amber-800',
      '#10B981': 'bg-green-100 text-green-800',
      '#EF4444': 'bg-red-100 text-red-800',
      '#F97316': 'bg-orange-100 text-orange-800',
      '#8B5CF6': 'bg-purple-100 text-purple-800',
      '#6B7280': 'bg-gray-100 text-gray-800'
    };
    return colorMap[hexColor] || 'bg-gray-100 text-gray-800';
  };

  // Filtrer les extras
  useEffect(() => {
    let filtered = extras;

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(extra =>
        extra.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        extra.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(extra => extra.category === selectedCategory);
    }

    setFilteredExtras(filtered);
  }, [extras, searchTerm, selectedCategory]);

  // Charger au montage
  useEffect(() => {
    loadCategories();
    loadExtras();
  }, []);

  // Sauvegarder un extra
  const saveExtra = async () => {
    try {
      if (formData.track_stock && !String(formData.inventory_kind || '').trim()) {
        showToast(
          'error',
          "Veuillez préciser si l'extra suivi est revendu ou composé.",
        );
        return;
      }
      const response = await fetch('/api/extras', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await loadExtras();
        setShowModal(false);
        resetForm();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde extra:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  // Retirer un extra du catalogue sans effacer les ventes passées
  const deleteExtra = async (id) => {
    if (!window.confirm("Retirer cet extra du catalogue ? Les anciennes ventes resteront intactes.")) {
      return;
    }

    try {
      const response = await fetch(`/api/extras/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await loadExtras();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('❌ Erreur suppression extra:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const toggleExtraAvailability = async (extra) => {
    if (!extra) return;
    const nextState = !extra.is_active;
    try {
      const patchResponse = await fetch(`/api/extras/${extra.id}/availability`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: nextState }),
      });

      if (!patchResponse.ok) {
        // Fallback for serveurs n'ayant pas la route PATCH
        if (patchResponse.status === 404) {
          const fallbackResponse = await fetch('/api/extras', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: extra.id,
              name: extra.name,
              description: extra.description || '',
              category: extra.category || 'Général',
              base_price: extra.base_price || 0,
              sort_order: extra.sort_order || 0,
              is_active: nextState,
              track_stock: extra.track_stock,
              inventory_kind: extra.inventory_kind || '',
            }),
          });

          if (!fallbackResponse.ok) {
            const error = await fallbackResponse.json().catch(() => ({}));
            throw new Error(
              error.error ||
                "Impossible de mettre à jour la disponibilité",
            );
          }
        } else {
          const error = await patchResponse.json().catch(() => ({}));
          throw new Error(
            error.error || "Impossible de mettre à jour la disponibilité",
          );
        }
      }

      setExtras((prev) =>
        nextState
          ? prev.map((item) =>
              item.id === extra.id ? { ...item, is_active: 1 } : item,
            )
          : prev.filter((item) => item.id !== extra.id),
      );
      showToast(
        'success',
        nextState ? 'Extra activé avec succès' : 'Extra désactivé avec succès',
      );
    } catch (error) {
      console.error('❌ Erreur lors du changement de disponibilité:', error);
      showToast(
        'error',
        error.message || 'Erreur lors du changement de disponibilité',
      );
    }
  };

  const toggleExtraTracking = async (extra) => {
    if (!extra) return;
    const nextState = !extra.track_stock;
    try {
      if (nextState && !String(extra.inventory_kind || '').trim()) {
        setTrackingKindChoice('composed');
        setTrackingKindPrompt({ extra });
        return;
      }
      const response = await fetch(`/api/extras/${extra.id}/track-stock`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          track_stock: nextState,
          inventory_kind: extra.inventory_kind || undefined,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          error.error || "Impossible de mettre à jour le suivi de stock",
        );
      }
      setExtras((prev) =>
        prev.map((item) =>
          item.id === extra.id ? { ...item, track_stock: nextState } : item,
        ),
      );
      showToast(
        'success',
        nextState
          ? 'Suivi de stock activé pour cet extra.'
          : 'Suivi de stock désactivé pour cet extra.',
      );
    } catch (error) {
      console.error('❌ Erreur suivi stock extra:', error);
      showToast(
        'error',
        error.message || 'Erreur lors du changement de suivi stock',
      );
    }
  };

  const openAssociations = async (extra) => {
    setAssociationExtra(extra);
    setAssociationSearch('');
    setAssociationLoading(true);
    try {
      const response = await fetch('/api/products/admin');
      if (!response.ok) throw new Error('Chargement impossible.');
      const products = await response.json();
      const activeProducts = (Array.isArray(products) ? products : []).filter(
        (product) => Number(product?.is_active ?? 1) === 1,
      );
      const draft = {};
      activeProducts.forEach((product) => {
        const rule = (product.extras || []).find(
          (item) => Number(item.id) === Number(extra.id),
        );
        draft[product.id] = {
          selected: Boolean(rule),
          type: rule?.type || (Number(extra.base_price) > 0 ? 'supplement' : 'accompaniment'),
          price: Number(rule?.price ?? extra.base_price) || 0,
          maxQuantity: Math.max(1, Number(rule?.max_quantity) || 1),
        };
      });
      setAssociationProducts(activeProducts);
      setAssociationDraft(draft);
      setAssociationInitial(JSON.parse(JSON.stringify(draft)));
    } catch (error) {
      showToast('error', error.message || 'Chargement impossible.');
      setAssociationExtra(null);
    } finally {
      setAssociationLoading(false);
    }
  };

  const updateAssociation = (productId, changes) => {
    setAssociationDraft((current) => ({
      ...current,
      [productId]: { ...current[productId], ...changes },
    }));
  };

  const saveAssociations = async () => {
    if (!associationExtra) return;
    const changed = associationProducts.filter((product) =>
      JSON.stringify(associationDraft[product.id]) !== JSON.stringify(associationInitial[product.id]),
    );
    setAssociationSaving(true);
    try {
      for (const product of changed) {
        const rule = associationDraft[product.id];
        const endpoint = `/api/products/${product.id}/extras/${associationExtra.id}`;
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(endpoint, rule.selected
          ? {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: rule.type,
                price: rule.type === 'supplement' ? Number(rule.price) || 0 : 0,
                maxQuantity: Math.max(1, Number(rule.maxQuantity) || 1),
              }),
            }
          : { method: 'DELETE' });
        if (!response.ok) throw new Error(`Association impossible : ${product.name}`);
      }
      showToast('success', 'Associations enregistrées.');
      setAssociationExtra(null);
    } catch (error) {
      showToast('error', error.message || 'Enregistrement impossible.');
    } finally {
      setAssociationSaving(false);
    }
  };

  const confirmTrackingKindPrompt = async () => {
    const target = trackingKindPrompt?.extra;
    if (!target) return;
    try {
      const response = await fetch(`/api/extras/${target.id}/track-stock`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ track_stock: true, inventory_kind: trackingKindChoice }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de mettre à jour le suivi de stock");
      }
      setExtras((prev) =>
        prev.map((item) =>
          item.id === target.id
            ? { ...item, track_stock: true, inventory_kind: trackingKindChoice }
            : item,
        ),
      );
      showToast('success', 'Suivi de stock activé pour cet extra.');
      setTrackingKindPrompt(null);
    } catch (error) {
      console.error('❌ Erreur activation suivi extra:', error);
      showToast('error', error.message || "Erreur lors de l'activation du suivi");
    }
  };

  // Ouvrir le modal d'édition
  const openEditModal = (extra = null) => {
    if (extra) {
      setEditingExtra(extra);
      setFormData({
        id: extra.id,
        name: extra.name,
        description: extra.description || '',
        category: extra.category || 'Général',
        base_price: extra.base_price,
        is_active: extra.is_active,
        track_stock: Boolean(extra.track_stock),
        inventory_kind: extra.inventory_kind || '',
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setEditingExtra(null);
    setFormData({
      name: '',
      description: '',
      category: 'Général',
      base_price: 0,
      is_active: true,
      track_stock: false,
      inventory_kind: '',
    });
  };

  // Obtenir les infos de catégorie
  const getCategoryInfo = (category) => {
    return categories.find(cat => cat.value === category) || categories[categories.length - 1];
  };

  // Formater le prix
  const formatPrice = (price) => {
    return `${new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(price) || 0)} FCFA`;
  };

  const containerBg = isDarkMode
    ? "text-slate-100"
    : "text-slate-900";
  const mutedText = isDarkMode ? "text-white/60" : "text-slate-500";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-200 shadow-lg";
  const badgeNeutral = isDarkMode
    ? "bg-white/10 text-white/80"
    : "bg-slate-100 text-slate-600";
  const buttonPrimary = "rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow-md shadow-amber-500/30 flex items-center gap-2";
  const inputClasses = isDarkMode
    ? "w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-sm text-slate-100 placeholder-white/40 focus:border-amber-400/60 focus:outline-none"
    : "w-full rounded-2xl border border-slate-200 px-4 py-2 text-sm text-slate-900 focus:border-amber-400/60 focus:outline-none";

  const stats = [
    { label: "Extras totaux", value: extras.length },
    { label: "Actifs", value: extras.filter((e) => e.is_active).length },
    { label: "Catégories", value: new Set(extras.map((e) => e.category)).size || categories.length },
  ];

  return (
    <div className={`space-y-6 ${containerBg}`}>
      <div className={`${cardClasses} rounded-3xl p-6 space-y-4`}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-300">
              Gestion des extras
            </p>
            <h2 className="text-2xl font-semibold tracking-tight">
              Accompagnements & suppléments
            </h2>
            <p className={`text-sm ${mutedText}`}>
              Configurez les options disponibles dans les fiches produits et la caisse.
            </p>
          </div>
          <button onClick={() => openEditModal()} className={buttonPrimary}>
            <Plus className="h-4 w-4" />
            Nouvel extra
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className={`rounded-2xl border ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"} px-4 py-3`}
            >
              <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
                {stat.label}
              </p>
              <p className="text-2xl font-semibold mt-2">{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      <section className={`${cardClasses} rounded-3xl p-6 space-y-4`}>
        <div className="flex flex-col gap-4 md:flex-row">
          <div className="relative flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
              size={18}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un extra ou une description..."
              className={`${inputClasses} pl-11`}
            />
          </div>
          <div className="relative md:w-64">
            <Filter
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
              size={18}
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`${inputClasses} appearance-none pl-11`}
            >
              <option value="all">Toutes les catégories</option>
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {[{ value: "all", label: "Toutes" }, ...categories].map((cat) => {
            const isActive = selectedCategory === cat.value;
            return (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  isActive
                    ? "bg-emerald-400 text-slate-900"
                    : badgeNeutral
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        {loading ? (
          <div className={`${cardClasses} rounded-3xl p-10 text-center`}>
            <div className="mx-auto h-12 w-12 rounded-full border-4 border-amber-400 border-b-transparent animate-spin" />
            <p className={`mt-4 text-sm ${mutedText}`}>Chargement des extras...</p>
          </div>
        ) : filteredExtras.length === 0 ? (
          <div className={`${cardClasses} rounded-3xl p-10 text-center`}>
            <Package className="mx-auto h-12 w-12 text-amber-300 mb-3" />
            <p className="text-lg font-semibold">Aucun extra trouvé</p>
            <p className={`text-sm ${mutedText}`}>
              Ajustez vos filtres ou créez votre première option.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredExtras.map((extra) => {
              const categoryInfo = getCategoryInfo(extra.category);
              return (
                <div
                  key={extra.id}
                  className={`${cardClasses} rounded-3xl p-5 space-y-4`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p
                        className={`text-xs uppercase tracking-[0.3em] ${
                          isDarkMode ? "text-white/60" : "text-slate-500"
                        }`}
                      >
                        {categoryInfo?.label || extra.category}
                      </p>
                      <h3 className="text-lg font-semibold">{extra.name}</h3>
                      {extra.description && (
                        <p className={`text-sm mt-1 ${mutedText}`}>
                          {extra.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        extra.is_active
                          ? "bg-emerald-400/20 text-emerald-300"
                          : "bg-rose-500/20 text-rose-200"
                      }`}
                    >
                      {extra.is_active ? "Actif" : "Inactif"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-amber-300" />
                      <span className="font-semibold">
                        {formatPrice(extra.base_price)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openAssociations(extra)}
                        className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
                        title="Associer aux produits"
                      >
                        <Link2 size={16} />
                      </button>
                      <button
                        onClick={() => toggleExtraTracking(extra)}
                        className={`rounded-full border border-white/10 p-2 ${
                          extra.track_stock
                            ? 'text-emerald-300 hover:border-emerald-400/60'
                            : 'text-amber-300 hover:border-amber-400/60'
                        }`}
                        title={
                          extra.track_stock
                            ? 'Désactiver le suivi de stock'
                            : 'Activer le suivi de stock'
                        }
                      >
                        <LocateFixed size={16} />
                      </button>
                      <button
                        onClick={() => openEditModal(extra)}
                        className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => toggleExtraAvailability(extra)}
                        className={`rounded-full border border-white/10 p-2 ${
                          extra.is_active
                            ? 'text-emerald-300 hover:border-emerald-400/60'
                            : 'text-amber-300 hover:border-amber-400/60'
                        }`}
                        title={
                          extra.is_active
                            ? 'Désactiver cet extra'
                            : 'Activer cet extra'
                        }
                      >
                        <Power size={16} />
                      </button>
                      <button
                        onClick={() => deleteExtra(extra.id)}
                        className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {associationExtra && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className={`w-full max-w-3xl max-h-[85vh] rounded-3xl border flex flex-col ${
            isDarkMode
              ? "bg-[#0f172a] border-white/10 text-slate-100"
              : "bg-white border-slate-200 text-slate-900"
          }`}>
            <div className="rounded-t-3xl bg-gradient-to-r from-amber-400 to-orange-500 p-5 text-slate-900 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em]">Associer</p>
                <h3 className="text-xl font-semibold mt-1">{associationExtra.name}</h3>
              </div>
              <button onClick={() => setAssociationExtra(null)} className="rounded-full bg-black/10 p-2 hover:bg-black/20">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 border-b border-white/10">
              <input
                type="search"
                value={associationSearch}
                onChange={(event) => setAssociationSearch(event.target.value)}
                placeholder="Rechercher un produit"
                className={inputClasses}
              />
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {associationLoading ? (
                <p className={mutedText}>Chargement...</p>
              ) : associationProducts
                  .filter((product) => product.name.toLowerCase().includes(associationSearch.toLowerCase()))
                  .map((product) => {
                    const rule = associationDraft[product.id] || {};
                    return (
                      <div key={product.id} className={`rounded-2xl border p-3 ${
                        isDarkMode ? 'border-white/10 bg-white/5' : 'border-slate-200'
                      }`}>
                        <div className="flex flex-wrap items-center gap-3">
                          <label className="flex min-w-[220px] flex-1 items-center gap-3 font-semibold">
                            <input
                              type="checkbox"
                              checked={Boolean(rule.selected)}
                              onChange={(event) => updateAssociation(product.id, { selected: event.target.checked })}
                            />
                            {product.name}
                          </label>
                          {rule.selected && (
                            <>
                              <select
                                value={rule.type}
                                onChange={(event) => updateAssociation(product.id, { type: event.target.value })}
                                className={`${inputClasses} w-auto`}
                              >
                                <option value="accompaniment">Accompagnement</option>
                                <option value="supplement">Supplément</option>
                              </select>
                              <input
                                type="number"
                                min="1"
                                value={rule.maxQuantity}
                                onChange={(event) => updateAssociation(product.id, { maxQuantity: event.target.value })}
                                className={`${inputClasses} w-20`}
                                title="Maximum"
                              />
                              {rule.type === 'supplement' && (
                                <input
                                  type="number"
                                  min="0"
                                  value={rule.price}
                                  onChange={(event) => updateAssociation(product.id, { price: event.target.value })}
                                  className={`${inputClasses} w-28`}
                                  title="Prix"
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setAssociationExtra(null)} className="rounded-2xl border border-white/10 px-4 py-2 font-semibold">
                Annuler
              </button>
              <button onClick={saveAssociations} disabled={associationSaving} className={buttonPrimary}>
                {associationSaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            className={`w-full max-w-lg rounded-3xl border ${
              isDarkMode
                ? "bg-[#0f172a] border-white/10 text-slate-100"
                : "bg-white border-slate-200 text-slate-900"
            }`}
          >
            <div className="rounded-t-3xl bg-gradient-to-r from-amber-400 to-orange-500 p-5 text-slate-900 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em]">
                  {editingExtra ? "Modifier" : "Ajouter"} un extra
                </p>
                <h3 className="text-xl font-semibold mt-1">
                  {editingExtra ? editingExtra.name : "Nouvel extra"}
                </h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-full bg-black/10 p-2 hover:bg-black/20"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className={`text-sm font-semibold mb-1 block ${mutedText}`}>
                  Nom de l'extra *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={inputClasses}
                  placeholder="Ex: Riz blanc, fromage..."
                />
              </div>

              <div>
                <label className={`text-sm font-semibold mb-1 block ${mutedText}`}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows="3"
                  className={`${inputClasses} resize-none`}
                  placeholder="Description optionnelle..."
                />
              </div>

              <div>
                <label className={`text-sm font-semibold mb-1 block ${mutedText}`}>
                  Prix de base (F CFA)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={formData.base_price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        base_price: parseFloat(e.target.value) || 0,
                      })
                    }
                    className={`${inputClasses} pl-10`}
                    placeholder="0"
                  />
                </div>
                <p className={`text-xs mt-1 ${mutedText}`}>
                  Prix de référence. Ajustable par produit.
                </p>
              </div>

              <label className="inline-flex items-center gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-white/20 text-emerald-400 focus:ring-emerald-400/40"
                />
                Extra actif
              </label>
              <label className="inline-flex items-center gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={formData.track_stock}
                  onChange={(e) =>
                    setFormData({ ...formData, track_stock: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-white/20 text-emerald-400 focus:ring-emerald-400/40"
                />
                Activer le suivi de stock
              </label>

              {formData.track_stock && (
                <div
                  className={`rounded-2xl border p-4 ${
                    isDarkMode
                      ? "border-white/10 bg-white/5"
                      : "border-amber-200 bg-amber-50/60"
                  }`}
                >
                  <p className="text-sm font-semibold">Nature de l'extra suivi</p>
                  <p className={`text-xs mt-1 ${mutedText}`}>
                    Choisissez revendu (tel quel) ou composé (via recette).
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {[
                      { id: "resold", label: "Produit revendu" },
                      { id: "composed", label: "Produit composé" },
                    ].map((opt) => (
                      <label
                        key={opt.id}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 border cursor-pointer ${
                          formData.inventory_kind === opt.id
                            ? "border-amber-400 bg-amber-400/20"
                            : isDarkMode
                              ? "border-white/10 bg-white/5"
                              : "border-slate-200 bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          name="inventory_kind"
                          value={opt.id}
                          checked={formData.inventory_kind === opt.id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              inventory_kind: e.target.value,
                            })
                          }
                          className="h-4 w-4"
                        />
                        <span className="text-sm font-semibold">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className={`rounded-b-3xl px-6 py-4 flex justify-end gap-3 border-t ${
                isDarkMode ? "border-white/5" : "border-slate-200"
              }`}
            >
              <button
                onClick={() => setShowModal(false)}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode
                    ? "border border-white/10 text-slate-200 hover:border-white/30"
                    : "border border-slate-200 text-slate-600 hover:border-slate-400"
                }`}
              >
                Annuler
              </button>
              <button
                onClick={saveExtra}
                disabled={!formData.name.trim()}
                className="rounded-2xl bg-gradient-to-r from-emerald-400 to-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="inline h-4 w-4 mr-2" />
                {editingExtra ? "Modifier" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {trackingKindPrompt && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end justify-center p-4 sm:items-center"
          role="presentation"
          onClick={() => setTrackingKindPrompt(null)}
        >
          <div
            className={`w-full max-w-lg rounded-3xl border ${
              isDarkMode
                ? "bg-[#0f172a] border-white/10 text-slate-100"
                : "bg-white border-slate-200 text-slate-900"
            }`}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-t-3xl bg-gradient-to-r from-amber-400 to-orange-500 p-5 text-slate-900">
              <p className="text-xs uppercase tracking-[0.3em]">Suivi de stock</p>
              <h3 className="text-xl font-semibold mt-1">Nature de l'extra suivi</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className={`text-sm ${mutedText}`}>
                Avant d'activer le suivi, précisez si cet extra est revendu tel quel ou composé via une recette.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  { id: 'resold', label: 'Produit revendu' },
                  { id: 'composed', label: 'Produit composé' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTrackingKindChoice(opt.id)}
                    className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                      trackingKindChoice === opt.id
                        ? 'border-amber-400 bg-amber-400/15'
                        : isDarkMode
                          ? 'border-white/10 bg-white/5 hover:border-white/20'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setTrackingKindPrompt(null)}
                  className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                    isDarkMode
                      ? "border border-white/10 text-slate-200 hover:border-white/30"
                      : "border border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={confirmTrackingKindPrompt}
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg"
                >
                  Activer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtrasManagement;
